import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clearAuth, getAuth } from '../../../features/auth/utils/authStorage';
import SearchBar from './SearchBar';
import { getMediaUrl } from '../../../utils/mediaUrl';

function BellIcon({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 17H9m10-2V11a7 7 0 1 0-14 0v4l-2 2h18l-2-2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 19a2 2 0 0 0 4 0"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MenuIcon({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Avatar({ user }) {
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || 'U';
  const photo = getMediaUrl(user?.profilePhoto || user?.profile?.profilePhoto);
  return (
    <div className="h-10 w-10 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 flex items-center justify-center text-sm font-bold text-navy">
      {photo ? <img src={photo} alt="" className="h-full w-full object-cover" onError={(event) => { event.currentTarget.style.display = 'none'; }} /> : initials}
    </div>
  );
}

function WorkerNavbar({ role = 'WORKER', mobileMenuItems = [] }) {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth?.user;

  const [query, setQuery] = React.useState('');
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const basePath = `/${role.toLowerCase()}`;

  React.useEffect(() => {
    const closeMenus = (event) => {
      if (!event.target.closest('[data-navbar-menu]')) {
        setProfileOpen(false);
        setMenuOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setProfileOpen(false);
        setMenuOpen(false);
      }
    };

    document.addEventListener('click', closeMenus);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('click', closeMenus);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const logout = () => {
    clearAuth();
    setProfileOpen(false);
    navigate('/login', { replace: true });
  };

  return (
    <nav data-navbar-menu className="fixed inset-x-0 top-0 z-40 w-full bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-card">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setMenuOpen((value) => !value);
              setProfileOpen(false);
            }}
            className="lg:hidden rounded-2xl border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-50"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <MenuIcon />
          </button>

          <Link to={basePath} className="flex items-center gap-2 transition hover:opacity-80 flex-shrink-0">
            <div className="relative h-10 w-10 overflow-hidden rounded-xl border border-slate-200 bg-transparent shadow-card">
              <img
                src={`${process.env.PUBLIC_URL}/images/logo.png`}
                alt="Tuma Jobs"
                className="h-full w-full object-cover"
              />
            </div>
            <p className="text-base font-bold tracking-tight text-navy">Tuma Jobs</p>
          </Link>
        </div>

        <SearchBar value={query} onChange={setQuery} />

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-2xl border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-50"
            aria-label="Notifications"
            onClick={() => navigate(
              role === 'CLIENT' ? `${basePath}/messages` : role === 'ADMIN' ? `${basePath}/logs` : '/worker/activity',
            )}
          >
            <BellIcon />
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setProfileOpen((value) => !value);
                setMenuOpen(false);
              }}
              className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-1 pr-3 text-slate-700 transition hover:bg-slate-50"
              aria-label="Profile menu"
            >
              <Avatar user={user} />
              <span className="hidden sm:inline text-sm font-semibold">
                {user?.firstName ? `${user.firstName}` : 'Profile'}
              </span>
            </button>

            {profileOpen && (
              <div className="absolute right-0 mt-2 w-48 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen(false);
                    navigate(`${basePath}/profile`);
                  }}
                  className="w-full px-4 py-3 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  View profile
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen(false);
                    navigate(`${basePath}/settings`);
                  }}
                  className="w-full px-4 py-3 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Settings
                </button>
                <button
                  type="button"
                  onClick={logout}
                  className="w-full px-4 py-3 text-left text-sm font-semibold text-rose-700 hover:bg-rose-50"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-4 py-3 space-y-2">
            {mobileMenuItems.map((item) => (
              <button
                key={item.to}
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  navigate(item.to);
                }}
                className="w-full rounded-2xl px-4 py-3 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

export default WorkerNavbar;

